import { FC, useState } from 'react';
import { Box, Typography, IconButton, Menu, MenuItem, Button, Chip } from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { openCommentDialog, commentDelete, commentSetStatus, commentPost } from '../../slices/commentSlice';
import { ReactionBar } from './reaction-bar';
import { MentionInput } from './mention-input';
import { MentionedText } from './mentioned-text';
import { formatDate } from '../../utils/format-date';

// Created today → just the time (e.g. "14:32"); otherwise the full date as
// "DD.MM.YYYY".
const formatCommentTime = (iso: string, lang: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return sameDay
    ? d.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })
    : formatDate(d);
};

interface CommentCardProps {
  comment: any;
  // All comments on the page (used to resolve nested replies).
  allComments?: any[];
  depth?: number;
  // Show a "Private" badge on non-shared comments (used in the drawer's "All"
  // filter mode; hidden in the private/shared-only modes where it is redundant).
  showPrivateBadge?: boolean;
}

export const CommentCard: FC<CommentCardProps> = ({ comment, allComments = [], depth = 0, showPrivateBadge = false }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyMentions, setReplyMentions] = useState<any[]>([]);

  const isCompleted = comment.status !== 0;
  const replies = allComments.filter((c) => c.replyToId === comment.id);

  const sendReply = () => {
    const msg = replyText.trim();
    if (!msg) return;
    // Backend inherits parentType/parentId from the target, so the reply stays
    // scoped to this page. Unlimited nesting. The reply inherits the parent's
    // privacy (isShared) so a "shared" reply can't surface under a private thread.
    dispatch(commentPost('reply', { message: replyText, replyToId: comment.id, importance: 0, isShared: comment.isShared, mentions: replyMentions }) as any);
    setReplyText('');
    setReplyMentions([]);
    setReplying(false);
  };

  return (
    <Box
      sx={{
        pl: depth > 0 ? 1.5 : 0,
        borderLeft: depth > 0 ? '2px solid' : 'none',
        borderColor: 'divider',
        mt: depth > 0 ? 0.5 : 0,
      }}
    >
      <Box sx={{ p: 0.75, mb: 0.5, borderRadius: 1, backgroundColor: 'background.paper', boxShadow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
            {comment.author_name || ''}
          </Typography>
          {showPrivateBadge && !comment.isShared && (
            <Chip size="small" variant="outlined" label={t('private')} sx={{ mr: 0.5, height: 20 }} />
          )}
          <Typography variant="caption" color="textSecondary" sx={{ mr: 0.5 }}>
            {formatCommentTime(comment.created_at, i18n.language)}
          </Typography>
          <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            <MenuItem
              onClick={() => {
                dispatch(openCommentDialog({ element: comment, isEditabled: true, isDuplicated: false }));
                setAnchorEl(null);
              }}
            >
              {t('edit')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                dispatch(commentSetStatus(comment, isCompleted ? 0 : 1) as any);
                setAnchorEl(null);
              }}
            >
              {isCompleted ? t('unarchive_comment') : t('archive_comment')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                dispatch(commentDelete(comment.id) as any);
                setAnchorEl(null);
              }}
            >
              {t('delete')}
            </MenuItem>
          </Menu>
        </Box>
        <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', mb: 0.25 }}>
          <MentionedText text={comment.message} />
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ReactionBar comment={comment} />
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size="small"
            onClick={() => setReplying((v) => !v)}
            sx={{
              // The theme pins every button to 12px vertical padding through a
              // nested `&.MuiButton-root` rule, so a bare `py` here is silently
              // outranked. Matching that selector is what actually shrinks this
              // row — and the button, not the text, is what set the card height.
              // 2px padding + the 24.5px line box still leaves a ~28px target.
              '&.MuiButton-root': { py: 0.25 },
              minWidth: 'auto',
            }}
          >
            {t('reply')}
          </Button>
        </Box>
        {replying && (
          <Box sx={{ mt: 1 }}>
            <MentionInput
              value={replyText}
              mentions={replyMentions}
              onChange={(text, m) => {
                setReplyText(text);
                setReplyMentions(m);
              }}
              placeholder={t('reply')}
              rows={2}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
              <Button size="small" variant="contained" onClick={sendReply} disabled={!replyText.trim()}>
                {t('send')}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
      {replies.map((r) => (
        <CommentCard key={r.id} comment={r} allComments={allComments} depth={depth + 1} showPrivateBadge={showPrivateBadge} />
      ))}
    </Box>
  );
};
